// ayusin mo ang sarili mo

import { Pagination, Table, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { ChangeEvent } from 'react';
import { X } from 'tabler-icons-react';
import { Currencies } from '../../enums/currencies.enum';
import { RetrieveAllTransactionsMode } from '../../enums/retrieve-transaction.enums';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import { TransactionColumns } from '../../type/filter.types';
import {
  BasicTransactionResponseDetails,
  BasicTransactionResponseSummary,
  RetrieveAllTransactionsRequest,
  TransactionStats,
} from '../../type/retrieve-transaction.types';
import TransactionStatistics from '../admin-transactions/transactions-statistics.component';

const tableCols: TransactionColumns = {
  cardType: 'Card type',
  transactionType: 'Transaction type',
  transactionStatus: 'Transaction status',
  industryMid: 'Industry MID',
  currency: 'Currency',
  transactionCount: 'Transaction count',
  amount: 'Total amount',
};
interface Props {
  data: BasicTransactionResponseSummary[];
  paginationData: IPaginationMeta | undefined;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setTransactionDetails: React.Dispatch<
    React.SetStateAction<BasicTransactionResponseDetails[] | undefined>
  >;
  setShowTransactionSummary: React.Dispatch<React.SetStateAction<boolean>>;
  setDetailsPaginationData: React.Dispatch<
    React.SetStateAction<IPaginationMeta | undefined>
  >;
  setTransactionDetailsRequest: React.Dispatch<
    React.SetStateAction<RetrieveAllTransactionsRequest | undefined>
  >;
  setIsLoadBranches: React.Dispatch<React.SetStateAction<boolean>>;
  transactionSummaryRequest: RetrieveAllTransactionsRequest | undefined;

  isChecked: number[];
  setIsChecked: React.Dispatch<React.SetStateAction<number[]>>;

  checkedAll: boolean;
  setCheckedAll: React.Dispatch<React.SetStateAction<boolean>>;
  transactionStats: TransactionStats | undefined;
}

function MerchantTransactionsTable(props: Props) {
  const {
    data,
    paginationData,
    setPage,
    page,
    setTransactionDetails,
    setShowTransactionSummary,
    setDetailsPaginationData,
    setTransactionDetailsRequest,
    setIsLoadBranches,
    transactionSummaryRequest,
    isChecked,
    setIsChecked,
    checkedAll,
    setCheckedAll,
    transactionStats,
  } = props;
  const axiosPrivate = useAxiosPrivate();

  const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const { id, checked } = e.target;

    if (checked) {
      setIsChecked([...isChecked, parseInt(id)]);
    } else {
      setIsChecked(isChecked.filter((item) => item !== parseInt(id)));
    }
  };

  const handleCheckAll = () => {
    if (!data) {
      return;
    }
    setCheckedAll(!checkedAll);

    if (checkedAll) {
      setIsChecked([]);
    } else {
      setIsChecked(Array.from(Array(data.length).keys()));
    }
  };

  const handleRowClick = async (selected: BasicTransactionResponseSummary) => {
    try {
      const request: RetrieveAllTransactionsRequest = {
        mode: RetrieveAllTransactionsMode.DETAILS,
        page: 1,
        filters: {
          ...transactionSummaryRequest!.filters,
        },
        detailedFilters: [
          {
            //overwrite the ones in transaction filter
            transactionStatusId: selected.transactionStatusId,
            industryMid: selected.industryMid,
            cardTypeId: selected.cardTypeId,
            currencyId: selected.currencyId,
            transactionTypeId: selected.transactionTypeId,
          },
        ],
      };

      const filters = request.filters;
      const detailedFilters = request.detailedFilters;
      if (!filters.limit) {
        delete filters.limit;
      }
      if (!filters.resellerId) {
        delete filters.resellerId;
      }
      if (!filters.merchantId) {
        delete filters.merchantId;
      }
      if (!filters.branchId) {
        delete filters.branchId;
      }
      if (!filters.posId) {
        delete filters.posId;
      }
      detailedFilters.forEach((detailedFilter) => {
        if (!detailedFilter.transactionStatusId) {
          delete detailedFilter.transactionStatusId;
        }
        if (!detailedFilter.industryMid) {
          delete detailedFilter.industryMid;
        }
      });

      const response = await axiosPrivate.post('/web/transactions', request);

      const transactionDetails: BasicTransactionResponseDetails[] =
        response.data.items;

      setTransactionDetails(transactionDetails);
      setDetailsPaginationData(response.data.metadata);
      setTransactionDetailsRequest(request);
      setShowTransactionSummary(false);
      setIsLoadBranches(false);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-transaction-details-branch',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    }
  };

  return (
    <>
      <div className='transaction-table'>
        <TransactionStatistics transactionStats={transactionStats} />

        <Table
          highlightOnHover
          sx={{
            '& thead tr th': { height: 50 },
          }}
          horizontalSpacing='md'
        >
          <thead style={{ maxWidth: 0 }}>
            <tr style={{ whiteSpace: 'nowrap', maxWidth: 0 }}>
              <th style={{ width: 30 }}>
                <input
                  type='checkbox'
                  style={{ cursor: 'pointer' }}
                  onClick={handleCheckAll}
                  checked={checkedAll}
                />
              </th>
              {Object.keys(tableCols).map((key: string) => (
                <th key={key}>{tableCols[key as keyof typeof tableCols]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((row, index) => (
              <tr key={index}>
                <td style={{ width: 30 }}>
                  <input
                    id={index.toString()}
                    type='checkbox'
                    onChange={handleCheck}
                    checked={isChecked.includes(index)}
                  />
                </td>
                <td onClick={() => handleRowClick(row)}>{row.cardTypeName}</td>
                <td onClick={() => handleRowClick(row)}>
                  {row.transactionTypeName}
                </td>
                <td onClick={() => handleRowClick(row)}>
                  {row.transactionStatusName}
                </td>
                <td>
                  {row.industryMid ? (
                    row.industryMid
                  ) : (
                    <Text color='dimmed' style={{ fontSize: '14px' }}>
                      N/A
                    </Text>
                  )}
                </td>
                <td onClick={() => handleRowClick(row)}>{row.currencyName}</td>
                <td onClick={() => handleRowClick(row)}>
                  {row.totalTransactionCount}
                </td>
                <td>
                  {Object.values(Currencies)[row.currencyId - 1] +
                    ' ' +
                    row.totalTransactionAmount.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {data.length > 0 && (
          <Pagination
            total={paginationData?.totalPages || 1}
            onChange={(value: number) => setPage(value)}
            page={page}
            position='center'
            p='md'
            spacing='md'
          />
        )}
      </div>
    </>
  );
}

export default MerchantTransactionsTable;
